import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, Upload, RefreshCw, Calendar, X, ChevronDown, ChevronsUpDown } from 'lucide-react';

type ReportTab = 'tappykash' | 'payment_verification' | 'voucher' | 'ewallet' | 'csv';
type TappySubTab = 'capture' | 'report';
type VoucherSubTab = 'unassign' | 'topup' | 'transactions';

interface ExpenseRow {
  id: number;
  fromDate: string;
  toDate: string;
  type: string;
  amount: string;
  currency: string;
}

function Toolbar({
  showSearch = true,
  showExport = true,
  showAddRecords = false,
  showFilter = false,
  showRefresh = false,
  onAddRecords,
}: {
  showSearch?: boolean;
  showExport?: boolean;
  showAddRecords?: boolean;
  showFilter?: boolean;
  showRefresh?: boolean;
  onAddRecords?: () => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-[20px] border border-[#ECEFF6] bg-white px-4 py-2.5">
      <div>
        {showSearch && (
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C97B5]" />
            <input
              type="text"
              placeholder="Search for Orders"
              className="h-9 w-[205px] rounded-full border border-[#DCE2F0] bg-[#FBFCFF] pl-9 pr-3 text-[13px] font-medium text-[#36415E] placeholder:text-[#9AA5C4] focus:outline-none focus:ring-2 focus:ring-yellow-400"
            />
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        {showExport && (
          <button className="inline-flex h-10 items-center gap-2 rounded-lg border border-[#F6C600] px-4 text-[14px] font-medium text-[#F1BE00] hover:bg-[#FFF9E1] transition-colors">
            <span>Export CSV</span>
            <Upload size={14} />
          </button>
        )}

        {showAddRecords && (
          <button
            onClick={onAddRecords}
            className="inline-flex h-10 items-center rounded-lg bg-[#F6C600] px-5 text-[14px] font-medium text-white hover:bg-[#e1b500] transition-colors"
          >
            Add Records
          </button>
        )}

        {showFilter && (
          <button className="inline-flex h-8 items-center gap-1 rounded-lg bg-[#FFF5CD] px-3 text-[12px] font-medium text-[#E5C24F] hover:bg-[#FCEBAC] transition-colors">
            <img src="/img/filter.svg" alt="Filter" className="h-3.5 w-3.5" />
            Filter
          </button>
        )}

        {showRefresh && (
          <button className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#F1F3F8] text-[#68779A] hover:bg-[#E6EAF4] transition-colors">
            <RefreshCw size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

function Pagination() {
  return (
    <div className="flex items-center justify-between px-4 py-3 text-xs text-[#5C6885] border-t border-[#EEF2FA]">
      <div className="flex items-center gap-2">
        <span>Show</span>
        <select
          defaultValue="5"
          className="h-7 rounded-md border border-[#DFE5F3] bg-[#F8FAFF] px-2 text-xs focus:outline-none focus:ring-1 focus:ring-yellow-400"
        >
          <option value="5">5</option>
          <option value="10">10</option>
          <option value="20">20</option>
        </select>
        <span>per page</span>
      </div>

      <div>1-10 of 52</div>

      <div className="flex items-center gap-1">
        <button className="rounded px-1.5 py-0.5 hover:bg-[#F3F6FF]">&larr;</button>
        {[1, 2, 3, 4, 5].map((page) => (
          <button
            key={page}
            className={`h-6 w-6 rounded text-xs ${
              page === 2 ? 'bg-[#EEF2FF] text-[#415178]' : 'text-[#5C6885] hover:bg-[#F3F6FF]'
            }`}
          >
            {page}
          </button>
        ))}
        <button className="rounded px-1.5 py-0.5 hover:bg-[#F3F6FF]">&rarr;</button>
      </div>
    </div>
  );
}

function HeaderCell({ label, className = '' }: { label: string; className?: string }) {
  return (
    <th
      className={`px-4 py-2.5 text-left text-[12px] font-medium text-[#5D6786] border-r border-[#E4E9F4] last:border-r-0 ${className}`}
    >
      <div className="flex items-center gap-1.5">
        <span>{label}</span>
        <ChevronsUpDown size={12} className="text-[#9BA8C9] shrink-0" />
      </div>
    </th>
  );
}

export default function ReportPage() {
  const [searchParams] = useSearchParams();

  const activeTabFromUrl = (searchParams.get('reportTab') as ReportTab) || 'tappykash';
  const [activeTab, setActiveTab] = useState<ReportTab>(activeTabFromUrl);
  const tappySubTab = (searchParams.get('tappySub') as TappySubTab) || 'capture';
  const voucherSubTab = (searchParams.get('voucherSub') as VoucherSubTab) || 'unassign';

  const [isAddRecordOpen, setIsAddRecordOpen] = useState(false);

  // TappyKash modal states
  const [provideOrderIdOpen, setProvideOrderIdOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [checkDetailsOpen, setCheckDetailsOpen] = useState(false);
  const [checkDetailsFlow, setCheckDetailsFlow] = useState<'capture' | 'topup'>('capture');
  const [checkDetailsPrimaryTab, setCheckDetailsPrimaryTab] = useState<'info' | 'secondary'>('info');
  const [checkDetailsSecondaryTab, setCheckDetailsSecondaryTab] = useState<'refund' | 'secondary'>('secondary');
  const [captureOrderId, setCaptureOrderId] = useState('');
  const [captureQrCode, setCaptureQrCode] = useState('');
  const [capturePaymentMethod, setCapturePaymentMethod] = useState<'delivery' | 'cash'>('delivery');
  const [expenseRows, setExpenseRows] = useState<ExpenseRow[]>([
    { id: 1, fromDate: '', toDate: '', type: 'Salaries', amount: '0', currency: '' },
    { id: 2, fromDate: '', toDate: '', type: 'Rents', amount: '0', currency: '' },
    { id: 3, fromDate: '', toDate: '', type: 'Office Expenses', amount: '0', currency: '' },
    { id: 4, fromDate: '', toDate: '', type: 'Local Transportation', amount: '0', currency: '' },
    { id: 5, fromDate: '', toDate: '', type: 'General Electricity + Generator costs', amount: '0', currency: '' },
    { id: 6, fromDate: '', toDate: '', type: 'Internet + Top ups', amount: '0', currency: '' },
    { id: 7, fromDate: '', toDate: '', type: 'Development fees', amount: '0', currency: '' },
  ]);

  const urlTab = (searchParams.get('reportTab') as ReportTab) || 'tappykash';
  if (urlTab !== activeTab) setActiveTab(urlTab);

  const handleExpenseFieldChange = (id: number, field: keyof ExpenseRow, value: string) => {
    setExpenseRows((rows) => rows.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  };

  const handleAddExpenseRow = () => {
    setExpenseRows((rows) => [
      ...rows,
      {
        id: rows.length ? rows[rows.length - 1].id + 1 : 1,
        fromDate: '',
        toDate: '',
        type: '',
        amount: '0',
        currency: '',
      },
    ]);
  };

  const handleRemoveExpenseRow = (id: number) => {
    setExpenseRows((rows) => rows.filter((row) => row.id !== id));
  };

  const renderTappyKash = () => (
    <div className="px-8">
      {tappySubTab === 'capture' ? (
        <div className="mx-auto flex min-h-[395px] w-full max-w-[904px] flex-col items-center justify-center rounded-[20px] border border-[#C4CADA] bg-white px-10 py-10 shadow-[0_4px_12px_rgba(0,0,0,0.07)]">
          <div className="w-full text-center">
            <h3 className="mb-4 text-[26px] font-semibold leading-tight text-[#111318]">
              Welcome to <span className="text-[#F0B704]">TapyKash</span>
            </h3>

            <div className="flex flex-row justify-center gap-5">
              {[
                { key: 'capture', label: 'Capture Order\nPayment', icon: '/img/Group 569.png' },
                { key: 'topup', label: 'Top Up', icon: '/img/Group 569 (1).png' },
                { key: 'cashout', label: 'Cash out', icon: '/img/Group 569 (2).png' },
              ].map((card) => (
                <button
                  key={card.key}
                  onClick={() => {
                    if (card.key === 'capture') setProvideOrderIdOpen(true);
                    else if (card.key === 'topup') setQrModalOpen(true);
                  }}
                  className="flex h-[160px] w-[150px] flex-col items-center justify-center gap-4 rounded-[12px] bg-[#F6C600] text-[#111827] shadow-[0_4px_10px_rgba(39,59,98,0.10)] transition-colors hover:bg-[#EABF0D]"
                >
                  <img src={card.icon} alt={card.label} className="h-[52px] w-[52px]" />
                  <span className="whitespace-pre-line px-2 text-center text-[14px] font-medium leading-[18px]">{card.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between px-2">
            <h2 className="text-[26px] leading-none font-medium text-[#1E2330]">TapyKash Report</h2>
            <div className="flex w-[460px] items-center justify-end gap-3">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C97B5]" />
                <input
                  type="text"
                  placeholder="Search for Orders"
                  className="h-9 w-[205px] rounded-full border border-[#DCE2F0] bg-[#FBFCFF] pl-9 pr-3 text-[13px] font-medium text-[#36415E] placeholder:text-[#9AA5C4] focus:outline-none focus:ring-2 focus:ring-yellow-400"
                />
              </div>
              <button className="inline-flex h-10 items-center gap-2 rounded-lg border border-[#F6C600] px-4 text-[14px] font-medium text-[#F1BE00] hover:bg-[#FFF9E1] transition-colors">
                <span>Export CSV</span>
                <Upload size={14} />
              </button>
            </div>
          </div>

          <div className="overflow-hidden rounded-[8px] border border-[#E6EBF5] bg-white">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-[#EEF2FF] border-b border-[#E4E9F4]">
                    <HeaderCell label="Order ID" />
                    <HeaderCell label="Date" />
                    <HeaderCell label="Customer Name" />
                    <HeaderCell label="Order Status" />
                    <HeaderCell label="Money Received" />
                    <HeaderCell label="Price" />
                    <HeaderCell label="Shipping Fee" />
                    <HeaderCell label="Agent Name" />
                    <HeaderCell label="Transaction ID" />
                    <HeaderCell label="Transaction Date" />
                    <HeaderCell label="Payment Date" />
                  </tr>
                </thead>
                <tbody>
                  {[
                    { status: 'Pending', statusClass: 'bg-[#FDEEF1] text-[#EF5079]' },
                    { status: 'Paid', statusClass: 'bg-[#E8F8EE] text-[#4BC77A]' },
                    { status: 'Paid', statusClass: 'bg-[#E8F8EE] text-[#4BC77A]' },
                    { status: 'Paid', statusClass: 'bg-[#E8F8EE] text-[#4BC77A]' },
                    { status: 'Paid', statusClass: 'bg-[#E8F8EE] text-[#4BC77A]' },
                  ].map((row, index) => (
                    <tr key={index} className="border-b border-[#EEF2FA] last:border-b-0 hover:bg-[#FAFCFF]">
                      <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">00-1027-69289</td>
                      <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">21 Oct, 2024</td>
                      <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">zainab albayati</td>
                      <td className="px-4 py-3 text-xs border-r border-[#E9EDF7]">
                        <span className={`inline-flex rounded-full px-3 py-1 text-[11px] font-medium ${row.statusClass}`}>{row.status}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">{index === 1 ? 'Received' : 'Not Received'}</td>
                      <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">1.12 USD</td>
                      <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">0.00 USD</td>
                      <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">salam.jamel</td>
                      <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">65634089dabc5</td>
                      <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">21 Oct, 2024</td>
                      <td className="px-4 py-3 text-xs text-[#344160]">{index === 0 ? 'Cash Payment' : 'Paid on delivery'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination />
          </div>
        </div>
      )}
    </div>
  );

  const renderPaymentVerification = () => (
    <div className="space-y-4 px-8">
      <Toolbar showSearch showExport showAddRecords showFilter onAddRecords={() => setIsAddRecordOpen(true)} />

      <div className="overflow-hidden rounded-[8px] border border-[#E6EBF5] bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-[#EEF2FF] border-b border-[#E4E9F4]">
                <th className="w-10 px-3 py-2.5 border-r border-[#E4E9F4]"><input type="checkbox" className="h-4 w-4 rounded border-[#CCD5EA]" /></th>
                <HeaderCell label="Actions" className="border-r-0" />
                <HeaderCell label="From" />
                <HeaderCell label="To" />
                <HeaderCell label="Creation Date" />
                <HeaderCell label="Total Amount" className="border-r-0" />
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, index) => (
                <tr key={index} className="border-b border-[#EEF2FA] last:border-b-0 hover:bg-[#FAFCFF]">
                  <td className="px-3 py-3 border-r border-[#E9EDF7]"><input type="checkbox" className="h-4 w-4 rounded border-[#CCD5EA]" /></td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#EEF2FF] px-3 py-1 text-[12px] font-medium text-[#7C8DEE]">
                      <img src="/img/eye.svg" alt="View" className="h-3 w-3" />
                      View All
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">21 Oct, 2024</td>
                  <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">21 Oct, 2024</td>
                  <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">21 Oct, 2024</td>
                  <td className="px-4 py-3 text-xs text-[#344160]">56.66 USD</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination />
      </div>

      {isAddRecordOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setIsAddRecordOpen(false)} />
          <div className="fixed right-0 top-0 z-50 flex h-full w-[860px] flex-col bg-white shadow-2xl">
            <div className="flex items-start justify-between bg-[#1E3A8A] px-6 py-5 text-white">
              <div>
                <h3 className="text-xl font-semibold">Add Record</h3>
                <p className="mt-1 text-sm text-white/80">Fill fields to add record</p>
              </div>
              <button className="rounded-full bg-[#426198] px-2 py-2" onClick={() => setIsAddRecordOpen(false)}>
                <span className="text-lg leading-none">x</span>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
              <div className="mb-6 grid grid-cols-2 gap-6">
                {['From Date', 'To Date'].map((label) => (
                  <div key={label}>
                    <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
                    <div className="relative">
                      <input type="text" placeholder="dd/mm/yyyy" className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
                      <Calendar size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8C97B5]" />
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-gray-900">Expense Type</h4>
                <div className="space-y-3">
                  {expenseRows.map((row) => (
                    <div key={row.id} className="grid grid-cols-[1.5fr,1fr,1fr,auto] items-center gap-3">
                      <input
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                        value={row.type}
                        onChange={(e) => handleExpenseFieldChange(row.id, 'type', e.target.value)}
                      />
                      <input
                        type="number"
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                        value={row.amount}
                        onChange={(e) => handleExpenseFieldChange(row.id, 'amount', e.target.value)}
                      />
                      <select
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                        value={row.currency}
                        onChange={(e) => handleExpenseFieldChange(row.id, 'currency', e.target.value)}
                      >
                        <option value="">Select</option>
                        <option value="USD">USD</option>
                        <option value="IQD">IQD</option>
                      </select>
                      <button className="rounded px-2 py-1 text-sm text-red-500 hover:bg-red-50" onClick={() => handleRemoveExpenseRow(row.id)}>x</button>
                    </div>
                  ))}
                </div>

                <button
                  className="mt-4 inline-flex items-center rounded-lg border border-dashed border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  onClick={handleAddExpenseRow}
                >
                  Add Row
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button className="rounded-lg bg-pink-100 px-4 py-2 text-sm font-medium text-red-600 hover:bg-pink-200" onClick={() => setIsAddRecordOpen(false)}>Cancel</button>
              <button className="rounded-lg bg-[#F6C600] px-6 py-2 text-sm font-semibold text-white hover:bg-[#e1b500]">Save</button>
            </div>
          </div>
        </>
      )}
    </div>
  );

  const renderVoucher = () => {
    const unassignColumns = ['ID', 'Gift Card Code', 'Initial Value', 'Current Value', 'Status', 'Expiry Date', 'Agent Name', 'Created At'];
    const topupColumns = ['ID', 'Gift Card Code', 'Initial Value', 'Current Value', 'Status', 'Expiry Date', 'User Name', 'Customer Name', 'Customer Email', 'Created At', 'Topup Date'];
    const txnColumns = ['ID', 'Gift Card Code', 'Order Number', 'Initial Value', 'Amount Used', 'Current Value', 'Used At', 'Customer Name', 'Customer Email', 'Agent Name'];
    const columns = voucherSubTab === 'unassign' ? unassignColumns : voucherSubTab === 'topup' ? topupColumns : txnColumns;

    return (
      <div className="space-y-4 px-8">
        <Toolbar showSearch showExport showFilter showRefresh />

        <div className="overflow-hidden rounded-[8px] border border-[#E6EBF5] bg-white">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-[#EEF2FF] border-b border-[#E4E9F4]">
                  <th className="w-10 px-3 py-2.5 border-r border-[#E4E9F4]"><input type="checkbox" className="h-4 w-4 rounded border-[#CCD5EA]" /></th>
                  {columns.map((col) => <HeaderCell key={col} label={col} />)}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 5 }).map((_, idx) => (
                  <tr key={idx} className="border-b border-[#EEF2FA] last:border-b-0 hover:bg-[#FAFCFF]">
                    <td className="px-3 py-3 border-r border-[#E9EDF7]"><input type="checkbox" className="h-4 w-4 rounded border-[#CCD5EA]" /></td>
                    <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">16576</td>
                    <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">7534-8872-5835-6654</td>
                    {voucherSubTab === 'unassign' ? (
                      <>
                        <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">56.66 USD</td>
                        <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">$0</td>
                        <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">Used</td>
                        <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">21 Oct, 2024</td>
                        <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">Test Name1</td>
                        <td className="px-4 py-3 text-xs text-[#344160]">21 Oct, 2024</td>
                      </>
                    ) : voucherSubTab === 'topup' ? (
                      <>
                        <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">$0</td>
                        <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">56.66 USD</td>
                        <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">Used</td>
                        <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">21 Oct, 2024</td>
                        <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">Test Name1</td>
                        <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">Test Name1</td>
                        <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">test@mahalkum.co</td>
                        <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">21 Oct, 2024</td>
                        <td className="px-4 py-3 text-xs text-[#344160]">{idx === 0 ? '2024-07-04' : '21 Oct, 2024'}</td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">1118-5015</td>
                        <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">$0</td>
                        <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">$0.02</td>
                        <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">$0</td>
                        <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">21 Oct, 2024</td>
                        <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">Test Name1</td>
                        <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">test@mahalkum.co</td>
                        <td className="px-4 py-3 text-xs text-[#344160]">Test Agent</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination />
        </div>
      </div>
    );
  };

  const renderEwallet = () => (
    <div className="space-y-4 px-8">
      <Toolbar showSearch showExport showFilter showRefresh />

      <div className="overflow-hidden rounded-[8px] border border-[#E6EBF5] bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-[#EEF2FF] border-b border-[#E4E9F4]">
                <th className="w-10 px-3 py-2.5 border-r border-[#E4E9F4]"><input type="checkbox" className="h-4 w-4 rounded border-[#CCD5EA]" /></th>
                {['ID', 'Customer Name', 'Customer Email', 'Date', 'Balance Charge', 'Balance', 'Transaction', 'Payment Method Title', 'Payment Method Code', 'Action', 'Additional Message', 'Customer Has Been Notified'].map((col) => (
                  <HeaderCell key={col} label={col} />
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, index) => (
                <tr key={index} className="border-b border-[#EEF2FA] last:border-b-0 hover:bg-[#FAFCFF]">
                  <td className="px-3 py-3 border-r border-[#E9EDF7]"><input type="checkbox" className="h-4 w-4 rounded border-[#CCD5EA]" /></td>
                  <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">16576</td>
                  <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">Test Name1</td>
                  <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">test@mahalkum.co</td>
                  <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">21 Oct, 2024</td>
                  <td className="px-4 py-3 text-xs border-r border-[#E9EDF7]">
                    <span className={index === 1 || index === 4 ? 'text-[#FF4A77] font-medium' : 'text-[#8EC63D] font-medium'}>
                      {index === 1 || index === 4 ? '-$290.5' : '+$38.26'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">38.26</td>
                  <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">54654-562263</td>
                  <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">Test User</td>
                  <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">test@mahalkum.co</td>
                  <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">Manual</td>
                  <td className="px-4 py-3 text-xs text-[#344160] border-r border-[#E9EDF7]">Test Agent</td>
                  <td className="px-4 py-3 text-xs text-[#344160]">Yes</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination />
      </div>
    </div>
  );

  const renderCsv = () => (
    <div className="mt-10 flex items-start justify-center">
      <div className="w-full max-w-[540px] rounded-[16px] border border-[#C9D2E6] bg-white px-14 py-8 shadow-[0_4px_10px_rgba(22,42,80,0.05)]">
        <h2 className="mb-8 text-center text-[26px] leading-none font-medium text-[#111318]">Order Report</h2>

        <div className="space-y-7">
          <select className="h-11 w-full rounded-lg border border-[#D5DDEF] px-3 text-[14px] font-normal text-[#26314E] focus:outline-none focus:ring-2 focus:ring-yellow-400">
            <option>Select CSV Type</option>
            <option>Orders</option>
            <option>Payments</option>
            <option>Wallet</option>
          </select>

          <select className="h-11 w-full rounded-lg border border-[#D5DDEF] px-3 text-[14px] font-normal text-[#26314E] focus:outline-none focus:ring-2 focus:ring-yellow-400">
            <option>Select a City</option>
            <option>Baghdad</option>
            <option>Erbil</option>
          </select>

          <div className="relative">
            <input
              type="text"
              placeholder="From"
              className="h-11 w-full rounded-lg border border-[#D5DDEF] px-3 text-[14px] font-normal text-[#26314E] placeholder:text-[#26314E] focus:outline-none focus:ring-2 focus:ring-yellow-400"
            />
            <Calendar size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7F8EB4]" />
          </div>

          <div className="relative">
            <input
              type="text"
              placeholder="To"
              className="h-11 w-full rounded-lg border border-[#D5DDEF] px-3 text-[14px] font-normal text-[#26314E] placeholder:text-[#26314E] focus:outline-none focus:ring-2 focus:ring-yellow-400"
            />
            <Calendar size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7F8EB4]" />
          </div>

          <div className="flex items-center gap-4">
            <span className="text-[16px] font-normal text-[#111318]">Include Test Order</span>
            <button className="relative h-6 w-12 rounded-full bg-[#F6C600]">
              <span className="absolute right-[2px] top-[2px] h-5 w-5 rounded-full bg-white" />
            </button>
          </div>
        </div>

        <div className="mt-8 flex justify-end">
          <button className="h-8 rounded-md bg-[#575EF5] px-6 text-[14px] font-normal text-white hover:bg-[#4a51db] transition-colors">Export</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-0 pb-10">
      {activeTab === 'tappykash' && renderTappyKash()}
      {activeTab === 'payment_verification' && renderPaymentVerification()}
      {activeTab === 'voucher' && renderVoucher()}
      {activeTab === 'ewallet' && renderEwallet()}
      {activeTab === 'csv' && renderCsv()}

      {/* ─── Provide Order ID drawer ─────────────────────────────── */}
      {provideOrderIdOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setProvideOrderIdOpen(false)} />
          <div className="fixed right-0 top-0 z-50 flex h-full w-[520px] flex-col bg-white shadow-2xl">
            <div className="flex items-start justify-between bg-[#1E3255] px-6 py-5 text-white">
              <div>
                <h3 className="text-[20px] font-semibold">Provide Order ID</h3>
                <p className="mt-1 text-[13px] text-white/70">Please provide your order ID</p>
              </div>
              <button
                onClick={() => setProvideOrderIdOpen(false)}
                className="rounded-full bg-[#2E4A7A] p-2 hover:bg-[#3A5A8A] transition-colors"
              >
                <X size={15} />
              </button>
            </div>
            <div className="flex-1 px-6 py-8">
              <label className="mb-1.5 block text-[13px] font-medium text-[#344160]">Enter Order ID</label>
              <input
                type="text"
                placeholder="Order ID"
                value={captureOrderId}
                onChange={(e) => setCaptureOrderId(e.target.value)}
                className="w-full rounded-lg border border-[#D5DDEF] px-3.5 py-2.5 text-[13px] text-[#26314E] placeholder:text-[#9AA5C4] focus:outline-none focus:ring-2 focus:ring-yellow-400"
              />
            </div>
            <div className="px-6 pb-8">
              <button
                onClick={() => {
                  setProvideOrderIdOpen(false);
                  setCheckDetailsFlow('capture');
                  setCheckDetailsPrimaryTab('info');
                  setCheckDetailsSecondaryTab('secondary');
                  setCheckDetailsOpen(true);
                }}
                className="w-full rounded-xl bg-[#F6C600] py-3 text-[15px] font-semibold text-[#1E2330] hover:bg-[#e5b800] transition-colors"
              >
                Submit Now
              </button>
            </div>
          </div>
        </>
      )}

      {/* ─── QR Code drawer (Top Up flow) ────────────────────────── */}
      {qrModalOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setQrModalOpen(false)} />
          <div className="fixed right-0 top-0 z-50 flex h-full w-[520px] flex-col bg-white shadow-2xl">
            <div className="flex items-start justify-between bg-[#1E3255] px-6 py-5 text-white">
              <div>
                <h3 className="text-[20px] font-semibold">QR Code</h3>
                <p className="mt-1 text-[13px] text-white/70">Please provide your QR code</p>
              </div>
              <button
                onClick={() => setQrModalOpen(false)}
                className="rounded-full bg-[#2E4A7A] p-2 hover:bg-[#3A5A8A] transition-colors"
              >
                <X size={15} />
              </button>
            </div>
            <div className="flex-1 px-6 py-8">
              <label className="mb-1.5 block text-[13px] font-medium text-[#344160]">Enter QR</label>
              <input
                type="text"
                placeholder="QR"
                value={captureQrCode}
                onChange={(e) => setCaptureQrCode(e.target.value)}
                className="w-full rounded-lg border border-[#D5DDEF] px-3.5 py-2.5 text-[13px] text-[#26314E] placeholder:text-[#9AA5C4] focus:outline-none focus:ring-2 focus:ring-yellow-400"
              />
            </div>
            <div className="px-6 pb-8">
              <button
                onClick={() => {
                  setQrModalOpen(false);
                  setCheckDetailsFlow('topup');
                  setCheckDetailsPrimaryTab('info');
                  setCheckDetailsSecondaryTab('secondary');
                  setCheckDetailsOpen(true);
                }}
                className="w-full rounded-xl bg-[#F6C600] py-3 text-[15px] font-semibold text-[#1E2330] hover:bg-[#e5b800] transition-colors"
              >
                Submit Now
              </button>
            </div>
          </div>
        </>
      )}

      {/* ─── Check Order Details drawer ──────────────────────────── */}
      {checkDetailsOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setCheckDetailsOpen(false)} />
          <div className="fixed right-0 top-0 z-50 flex h-full w-[620px] flex-col bg-white shadow-2xl">
            {/* Header */}
            <div className="flex items-start justify-between bg-[#1E3255] px-6 py-5 text-white">
              <div>
                <h3 className="text-[20px] font-semibold">Check Order Details</h3>
                <p className="mt-1 text-[13px] text-white/70">Please check your order details</p>
              </div>
              <button
                onClick={() => setCheckDetailsOpen(false)}
                className="rounded-full bg-[#2E4A7A] p-2 hover:bg-[#3A5A8A] transition-colors"
              >
                <X size={15} />
              </button>
            </div>

            {/* Primary Tab Row */}
            <div className="flex border-b border-[#E8ECF5] px-6 pt-4">
              {[
                { key: 'info', label: 'Order Information' },
                { key: 'secondary', label: checkDetailsFlow === 'capture' ? 'Invoice' : 'QR' },
              ].map((tab) => {
                const isActive = checkDetailsPrimaryTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setCheckDetailsPrimaryTab(tab.key as 'info' | 'secondary')}
                    className={`px-5 py-2.5 text-[13px] font-medium transition-colors ${
                      isActive
                        ? 'border-b-2 border-[#F6C600] text-[#BF9A00]'
                        : 'text-[#6B7896] hover:text-[#344160]'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-6">
              {checkDetailsPrimaryTab === 'info' ? (
                checkDetailsFlow === 'capture' ? (
                  /* ── Capture – Order Information ── */
                  <div className="space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="mb-1 block text-[12px] font-medium text-[#5D6786]">Enter Order ID</label>
                        <input
                          type="text"
                          defaultValue={captureOrderId}
                          placeholder="Order ID"
                          className="w-full rounded-lg border border-[#D5DDEF] px-3 py-2.5 text-[13px] text-[#26314E] placeholder:text-[#9AA5C4] focus:outline-none focus:ring-2 focus:ring-yellow-400"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-[12px] font-medium text-[#5D6786]">Order Date</label>
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="No Status"
                            className="w-full rounded-lg border border-[#D5DDEF] px-3 py-2.5 text-[13px] text-[#26314E] placeholder:text-[#9AA5C4] focus:outline-none focus:ring-2 focus:ring-yellow-400"
                          />
                          <Calendar size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8C97B5]" />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block text-[12px] font-medium text-[#5D6786]">Order Date</label>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex overflow-hidden rounded-lg border border-[#D5DDEF]">
                          <input
                            type="number"
                            placeholder="0"
                            className="flex-1 px-3 py-2.5 text-[13px] text-[#26314E] focus:outline-none"
                          />
                          <div className="flex items-center border-l border-[#D5DDEF] bg-[#F8FAFF] px-3 text-[12px] text-[#5D6786]">USD</div>
                        </div>
                        <div className="flex overflow-hidden rounded-lg border border-[#D5DDEF]">
                          <input
                            type="number"
                            defaultValue={5320}
                            className="flex-1 px-3 py-2.5 text-[13px] text-[#26314E] focus:outline-none"
                          />
                          <div className="flex items-center border-l border-[#D5DDEF] bg-[#F8FAFF] px-3 text-[12px] text-[#5D6786]">IQD</div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="mb-2 block text-[12px] font-medium text-[#5D6786]">Payment Method</label>
                      <div className="flex gap-3">
                        <button
                          onClick={() => setCapturePaymentMethod('delivery')}
                          className={`rounded-full px-5 py-2 text-[13px] font-medium transition-colors ${
                            capturePaymentMethod === 'delivery'
                              ? 'bg-[#F6C600] text-[#4A3800]'
                              : 'bg-[#F1F3F8] text-[#5D6786] hover:bg-[#E6EAF4]'
                          }`}
                        >
                          Paid on Delivery
                        </button>
                        <button
                          onClick={() => setCapturePaymentMethod('cash')}
                          className={`rounded-full px-5 py-2 text-[13px] font-medium transition-colors ${
                            capturePaymentMethod === 'cash'
                              ? 'bg-[#F6C600] text-[#4A3800]'
                              : 'bg-[#F1F3F8] text-[#5D6786] hover:bg-[#E6EAF4]'
                          }`}
                        >
                          Cash Payment
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block text-[12px] font-medium text-[#5D6786]">Payment Description</label>
                      <textarea
                        rows={3}
                        placeholder="Add Payment Description"
                        className="w-full resize-none rounded-lg border border-[#D5DDEF] px-3 py-2.5 text-[13px] text-[#26314E] placeholder:text-[#9AA5C4] focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      />
                    </div>
                  </div>
                ) : (
                  /* ── TopUp – Order Information ── */
                  <div className="space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="mb-1 block text-[12px] font-medium text-[#5D6786]">QR Generation Date</label>
                        <div className="relative">
                          <input
                            type="text"
                            defaultValue="2025-01-11 15:23:42"
                            className="w-full rounded-lg border border-[#D5DDEF] px-3 py-2.5 text-[13px] text-[#26314E] focus:outline-none focus:ring-2 focus:ring-yellow-400"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="mb-1 block text-[12px] font-medium text-[#5D6786]">Status</label>
                        <div className="relative">
                          <select className="w-full appearance-none rounded-lg border border-[#D5DDEF] bg-white px-3 py-2.5 text-[13px] text-[#26314E] focus:outline-none focus:ring-2 focus:ring-yellow-400">
                            <option>Pending</option>
                            <option>Completed</option>
                            <option>Cancelled</option>
                          </select>
                          <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#8C97B5]" />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="mb-1 block text-[12px] font-medium text-[#5D6786]">Customer Name</label>
                        <input
                          type="text"
                          defaultValue="Testing Test"
                          className="w-full rounded-lg border border-[#D5DDEF] px-3 py-2.5 text-[13px] text-[#26314E] focus:outline-none focus:ring-2 focus:ring-yellow-400"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-[12px] font-medium text-[#5D6786]">Phone Number</label>
                        <input
                          type="text"
                          defaultValue="8296008530"
                          className="w-full rounded-lg border border-[#D5DDEF] px-3 py-2.5 text-[13px] text-[#26314E] focus:outline-none focus:ring-2 focus:ring-yellow-400"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block text-[12px] font-medium text-[#5D6786]">Top up Amount</label>
                      <div className="flex overflow-hidden rounded-lg border border-[#D5DDEF]">
                        <input
                          type="number"
                          defaultValue={5320}
                          className="flex-1 px-3 py-2.5 text-[13px] text-[#26314E] focus:outline-none"
                        />
                        <div className="flex items-center border-l border-[#D5DDEF] bg-[#F8FAFF] px-3 text-[12px] text-[#5D6786]">USD</div>
                      </div>
                    </div>

                    <div>
                      <label className="mb-1 block text-[12px] font-medium text-[#5D6786]">Payment Description</label>
                      <textarea
                        rows={3}
                        placeholder="Add Payment Description"
                        className="w-full resize-none rounded-lg border border-[#D5DDEF] px-3 py-2.5 text-[13px] text-[#26314E] placeholder:text-[#9AA5C4] focus:outline-none focus:ring-2 focus:ring-yellow-400"
                      />
                    </div>
                  </div>
                )
              ) : (
                /* ── Secondary tab (Invoice or QR) ── */
                <div className="space-y-5">
                  {/* Secondary sub-tabs */}
                  <div className="flex overflow-hidden rounded-full border border-[#E3E7F2] w-fit">
                    {[
                      { key: 'refund', label: 'Refund Item' },
                      { key: 'secondary', label: checkDetailsFlow === 'capture' ? 'Invoice' : 'QR' },
                    ].map((st, idx) => {
                      const isActive = checkDetailsSecondaryTab === st.key;
                      return (
                        <button
                          key={st.key}
                          onClick={() => setCheckDetailsSecondaryTab(st.key as 'refund' | 'secondary')}
                          className={`px-5 py-2 text-[13px] font-medium transition-colors ${
                            idx > 0 ? 'border-l border-[#E3E7F2]' : ''
                          } ${
                            isActive
                              ? 'bg-[#F6C600] text-[#4A3800]'
                              : 'bg-white text-[#5D6786] hover:bg-[#F7F8FB]'
                          }`}
                        >
                          {st.label}
                        </button>
                      );
                    })}
                  </div>

                  <div>
                    <label className="mb-1 block text-[12px] font-medium text-[#5D6786]">
                      {checkDetailsFlow === 'capture' ? 'Enter Order Id' : 'Enter QR'}
                    </label>
                    <input
                      type="text"
                      placeholder={checkDetailsFlow === 'capture' ? 'Order ID' : 'QR'}
                      defaultValue={checkDetailsFlow === 'capture' ? captureOrderId : captureQrCode}
                      className="w-full rounded-lg border border-[#D5DDEF] px-3 py-2.5 text-[13px] text-[#26314E] placeholder:text-[#9AA5C4] focus:outline-none focus:ring-2 focus:ring-yellow-400"
                    />
                  </div>

                  <button className="w-full rounded-xl bg-[#F6C600] py-3 text-[15px] font-semibold text-[#1E2330] hover:bg-[#e5b800] transition-colors">
                    Submit Now
                  </button>
                </div>
              )}
            </div>

            {/* Capture button (only on Order Information tab) */}
            {checkDetailsPrimaryTab === 'info' && (
              <div className="border-t border-[#E8ECF5] px-6 py-4">
                <button className="w-full rounded-xl bg-[#F6C600] py-3 text-[15px] font-semibold text-[#1E2330] hover:bg-[#e5b800] transition-colors">
                  Capture
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
